import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import type { ButtonHTMLAttributes, ReactNode } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
  children: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  className,
  disabled,
  children,
  ...props
}: ButtonProps) {
  return (
    <motion.button
      whileHover={disabled || loading ? undefined : { scale: 1.02 }}
      whileTap={disabled || loading ? undefined : { scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={cn(
        'inline-flex items-center justify-center font-medium rounded-xl cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        {
          'bg-accent hover:bg-accent-hover text-white font-semibold': variant === 'primary',
          'bg-white hover:bg-card-hover text-text-primary border border-border shadow-sm': variant === 'secondary',
          'bg-danger/10 hover:bg-danger/15 text-danger border border-danger/15': variant === 'danger',
          'hover:bg-black/[0.04] text-text-secondary hover:text-text-primary': variant === 'ghost',
        },
        {
          'px-3.5 py-1.5 text-sm': size === 'sm',
          'px-5 py-2.5 text-[15px]': size === 'md',
          'px-7 py-3 text-base': size === 'lg',
        },
        className
      )}
      disabled={disabled || loading}
      {...(props as any)}
    >
      {loading && (
        <svg className="animate-spin -ml-0.5 mr-2 h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </motion.button>
  )
}
